import React, { useState, useEffect } from "react";
import { Phone, ArrowRight, Shield, CheckCircle, Mail, Clock } from "lucide-react";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "../firebase";
import { useNavigate } from "react-router-dom";
import open from "../assets/Treat_box_1.jpg";
import image3 from "../assets/Treat_box_2.jpg";
import image2 from "../assets/TreatBox.jpg";
import Logo from "../assets/logo.png";
import crack from '../assets/crack1.png'

export const LoginPage = ({ onLogin }) => {
  const [step, setStep] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("+91 ");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const diwaliTaglines = [
    "Pre-book now for ₹99. Pay ₹449 during pickup. Get sweets worth ₹550 + FREE Halwa worth ₹130!",
    "Limited Stock. Pre-book before October 15 to secure your box!"
  ];

  const [taglineIndex, setTaglineIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false); // fade out
      setTimeout(() => {
        setTaglineIndex((prev) => (prev + 1) % diwaliTaglines.length);
        setFade(true); // fade in
      }, 500); // match with fade duration
    }, 5000);
    return () => clearInterval(interval);
  }, []);



  const slides = [
    {
      image: open,
      title: "Capturing Moments,",
      subtitle: "Creating Memories",
      description: "Experience the finest loyalty program with exclusive benefits",
    },
    {
      image: image2,
      title: "Exclusive Rewards,",
      subtitle: "Premium Benefits",
      description: "Earn points and get amazing member-only privileges",
    },
    {
      image: image3,
      title: "Premium Membership,",
      subtitle: "VIP Experience",
      description: "Join our elite loyalty club for personalized service",
    },
  ];

  // Disable body scroll
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      document.body.style.overflow = "auto";
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Auto-rotate slideshow
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(slideInterval);
  }, [slides.length]);

  // reCAPTCHA setup
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        badge: "bottomright",
      });
      window.recaptchaVerifier.render();
    }
  }, []);

  // Timer logic
  useEffect(() => {
    if (step === "otp" && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step, timer]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError("");

    let formattedPhone = phoneNumber.replace(/\D/g, "");
    if (!formattedPhone.startsWith("91")) formattedPhone = "91" + formattedPhone;
    formattedPhone = "+" + formattedPhone.slice(0, 12);

    if (formattedPhone.length !== 13) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    try {
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setStep("otp");
      setTimer(30);
      setCanResend(false);
    } catch (err) {
      console.error(err);
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const result = await confirmationResult.confirm(otpValue);
      onLogin(result.user.phoneNumber);
      navigate("/");
    } catch (err) {
      console.error(err);
      setError("Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      document.getElementById(`otp-${index - 1}`)?.focus();
  };

  const handleResendOtp = () => {
    setTimer(30);
    setCanResend(false);
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setStep("phone");
  };

  const openPolicy = (type) => {
    switch (type) {
      case "/terms":
        navigate("/terms");
        break;
      case "/refund":
        navigate("/refund");
        break;
      case "/privacy":
        navigate("/privacy");
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-hidden">
      {/* Mobile View */}
      <div className="lg:hidden relative h-64 sm:h-80">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover rounded-b-3xl"
            />
            <div className="absolute inset-0 bg-black/50 rounded-b-3xl"></div>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-center px-6 max-w-md">
                <h1 className="text-3xl font-bold mb-2 leading-snug">
                  {slide.title} <br />
                  <span className="text-yellow-300">{slide.subtitle}</span>
                </h1>
                <p className="text-gray-200 text-base leading-relaxed mb-4">
                  {slide.description}
                </p>

                <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? "bg-yellow-400 scale-125" : "bg-white/50 hover:bg-white/80"
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content - Split Screen */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Desktop Slideshow */}
        <div className="hidden lg:flex lg:w-7/12 relative">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? "opacity-100" : "opacity-0"
                }`}
            >
              <div className="w-full h-full rounded-l-3xl rounded-r-3xl overflow-hidden">
                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-black/40 rounded-l-3xl rounded-r-3xl"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-white text-center px-8 max-w-2xl">
                  <h1 className="text-5xl font-bold mb-4 leading-tight">
                    {slide.title}
                    <br />
                    <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent drop-shadow-[2px_2px_2px_black]">{slide.subtitle}</span>
                  </h1>
                  <p className="text-xl text-gray-200 mb-8 leading-relaxed">{slide.description}</p>
                  <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-3">
                    {slides.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 ${idx === currentSlide ? "bg-yellow-400 scale-125" : "bg-white/50"
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Side - Login Card */}
        <div className="flex-1 lg:w-5/12 flex items-center justify-center p-4 sm:p-6 lg:p-8 rounded-2xl">
          <div className="w-full max-w-md -mt-5 lg:-mt-6">
            <div className="text-center mb-6 lg:mb-2 flex flex-col items-center">
              <img src={Logo} alt="Aaladipattiyan" className="max-h-24 mb-4" />

              <section className="text-center max-w-md">
                <h1 className="text-xl font-bold mb-2">Pre-Book Your Diwali Delights</h1>
                <p
                  className={`text-yellow-600 font-semibold text-2md sm:text-md mt-3 
                    leading-snug min-h-[3.5rem] sm:min-h-[3.25rem]
                    transition-opacity duration-500 ease-in-out ${fade ? "opacity-100" : "opacity-0"}`}
                >
                  {diwaliTaglines[taglineIndex]}
                </p>

              </section>
              <p className="text-gray-600 mt-2 text-sm lg:text-base">
                {step === "phone" ? "Sign in to your rewards account!" : "Verify your OTP"}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 lg:p-8">
              {step === "phone" ? (
                <form onSubmit={handlePhoneSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Phone Number</label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, "");
                          if (value.startsWith("91")) value = value.slice(2);
                          setPhoneNumber("+91 " + value.slice(0, 10));
                        }}
                        className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 outline-none transition-all"
                        placeholder="+91 9876543210"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <Phone size={20} className="text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">{error}</div>}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 px-6 rounded-lg font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                  >
                    {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <>
                      Send OTP
                      <ArrowRight size={18} />
                    </>}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleOtpSubmit} className="space-y-6">
                  <div className="text-center">
                    {/* <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Shield size={28} className="text-white lg:size-8" />
                    </div> */}
                    {/* <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2">Verify OTP</h3> */}
                    <p className="text-gray-600 mb-6 text-sm lg:text-base">
                      Code sent to <span className="font-semibold text-gray-900">{phoneNumber}</span>
                    </p>

                    <div className="flex gap-2 lg:gap-3 justify-center mb-6">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-9 h-9 lg:w-9 lg:h-9 text-center text-lg lg:text-xl font-bold border border-gray-300 rounded-lg focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 outline-none transition-all"
                          maxLength={1}
                          inputMode="numeric"
                        />
                      ))}
                    </div>

                    {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">{error}</div>}

                    <div className="text-center mb-4">
                      {canResend ? (
                        <button type="button" onClick={handleResendOtp} className="text-yellow-600 hover:text-yellow-700 font-medium text-sm underline">
                          Resend OTP
                        </button>
                      ) : (
                        <p className="text-gray-500 text-sm">Resend OTP in <span className="font-mono text-yellow-500">{timer}s</span></p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || otp.join("").length !== 6}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 px-6 rounded-lg font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 mb-4"
                    >
                      {isLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <>
                        <CheckCircle size={18} />
                        Verify & Continue
                      </>}
                    </button>

                    <button
                      type="button"
                      onClick={() => setStep("phone")}
                      className="text-gray-600 hover:text-gray-800 font-medium text-sm flex items-center justify-center gap-2 mx-auto"
                    >
                      ← Use different number
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="mt-6 lg:mt-8 text-center text-sm text-gray-600">
              {step === "phone" && (
                <div className="grid grid-cols-1 gap-2 mb-4">
                  <div className="flex items-center justify-center gap-2"><Phone size={14} /><span>+91 93614 61549</span></div>
                  <div className="flex items-center justify-center gap-2"><Mail size={14} /><span>support@halwakadai.com</span></div>
                  <div className="flex items-center justify-center gap-2"><Clock size={14} /><span>9:00 AM - 7:00 PM</span></div>
                </div>
              )}

              <footer className="text-center p-6 bg-white text-gray-600 text-xs">
                <div className="flex flex-wrap justify-center gap-4 mb-2">
                  <button
                    type="button" // Add this
                    onClick={() => openPolicy("/privacy")}
                    className="underline text-gray-600 hover:text-gray-800"
                  >
                    Privacy Policy
                  </button>
                  <button
                    type="button" // Add this
                    onClick={() => openPolicy("/terms")}
                    className="underline text-gray-600 hover:text-gray-800"
                  >
                    Terms & Conditions
                  </button>
                  <button
                    type="button" // Add this
                    onClick={() => openPolicy("/refund")}
                    className="underline text-gray-600 hover:text-gray-800"
                  >
                    Refund & Cancellation
                  </button>
                </div>
                <p>© 2025 Aaladipattiyan Private Limited</p>
              </footer>
            </div>
          </div>
        </div>
      </div>

      <div id="recaptcha-container" className="hidden"></div>
    </div>
  );
};

export default LoginPage;